import React from "react";
import { Button, Card, CardHeader } from "@opendate-app/setlist";

import { useBroadcastEditContext } from "../BroadcastEditContainer";
import PhonePreview from "../Composer/PhonePreview";
import useRenderedSms from "../Composer/useRenderedSms";
import { senderLabel } from "../Composer/smsMessage";
import { smsSegments } from "../Composer/smsSegments";
import { formatCount, formatPercent, prettyUrl } from "./reportMath";

const styles = {
  layout: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 16,
    alignItems: "start",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: 8,
    padding: "8px 0",
    fontSize: 14,
    borderTop: "1px solid var(--sl-border-secondary)",
  },
  muted: {
    fontSize: 13,
    color: "var(--sl-text-tertiary)",
  },
};

const ReportMessage = ({ onDuplicate }) => {
  const { broadcast, twilioMessagingServices, overviewMetrics, overviewLinkClicks } = useBroadcastEditContext();
  const template = broadcast.twilio_template.template;
  const rendered = useRenderedSms(template);
  const sender = twilioMessagingServices?.find((service) => service.id === broadcast.twilio_template.twilio_messaging_service_id);
  const { length, segments } = smsSegments(rendered.text ?? template);

  const links = overviewLinkClicks || [];
  const totalClicks = links.reduce((sum, link) => sum + link.clicks, 0);

  const facts = [
    ["From", sender ? senderLabel(sender) : "—"],
    ["Length", `${formatCount(length)} characters`],
    ["Segments", `${formatCount(segments)} per text`],
    ["Type", "SMS"],
  ];

  return (
    <div style={styles.layout}>
      <PhonePreview draft={template} rendered={rendered} fanCount={formatCount(overviewMetrics.sent_count)} />

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {links.length > 0 && (
          <Card header={<CardHeader title="Clicks by link" supportingText={`${formatCount(totalClicks)} tracked clicks`} />}>
            {links.map((link) => (
              <div key={link.url} style={styles.row}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={link.url}>{prettyUrl(link.url)}</span>
                <span>{formatCount(link.clicks)} <span style={styles.muted}>{formatPercent(link.clicks, totalClicks)}</span></span>
              </div>
            ))}
          </Card>
        )}

        <Card header={<CardHeader title="Message" />}>
          {facts.map(([label, value]) => (
            <div key={label} style={styles.row}>
              <span style={styles.muted}>{label}</span>
              <span>{value}</span>
            </div>
          ))}
        </Card>

        <Card header={<CardHeader title="Reuse this text" />}>
          <p style={styles.muted}>Sent texts can't be edited. Start a new SMS broadcast with this message.</p>
          <Button variant="link-color" size="sm" onClick={onDuplicate}>Duplicate to a new broadcast</Button>
        </Card>
      </div>
    </div>
  );
};

export default ReportMessage;
